/**
 * Sidebar Store
 *
 * Zustand store for sidebar state — split panel layout with Explorer (top)
 * and Scripts (bottom), draggable divider, and collapsible panels.
 * Persists layout state across app restarts.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createGuardedJsonStorage } from '../../../core/storage/guardedJsonStorage';

/** Track analytics event for sidebar state changes */
const trackSidebarEvent = (action: 'expand' | 'collapse') => {
  if (typeof window !== 'undefined' && window.electronAPI?.analytics?.track) {
    window.electronAPI.analytics.track(`explorer:${action}`).catch(() => {});
  }
};

/** Default sidebar width in pixels */
const DEFAULT_SIDEBAR_WIDTH = 288; // 18rem = 288px

/** Minimum sidebar width */
const MIN_SIDEBAR_WIDTH = 192;

/** Maximum sidebar width */
const MAX_SIDEBAR_WIDTH = 512;

/** Default split ratio — fraction of height given to Explorer */
const DEFAULT_SPLIT_RATIO = 0.6;

/** Min/max split ratio to keep both panels usable */
const MIN_SPLIT_RATIO = 0.15;
const MAX_SPLIT_RATIO = 0.85;

interface SidebarState {
  /** Whether the sidebar is collapsed */
  isCollapsed: boolean;
  /** Sidebar width in pixels (when expanded) */
  width: number;
  /** Fraction of sidebar height given to Explorer (0–1) */
  sidebarSplitRatio: number;
  /** Whether the Explorer panel is collapsed to just its header */
  explorerCollapsed: boolean;
  /** Whether the Scripts panel is collapsed to just its header */
  scriptsCollapsed: boolean;
  /** Whether the store has been hydrated from localStorage */
  hasHydrated: boolean;

  // Actions
  toggleCollapsed: () => void;
  expand: () => void;
  collapse: () => void;
  setWidth: (width: number) => void;
  setSplitRatio: (ratio: number) => void;
  toggleExplorerCollapsed: () => void;
  toggleScriptsCollapsed: () => void;
  expandScriptsPanel: () => void;
  setHasHydrated: (hydrated: boolean) => void;
}

/** Clamp split ratio to valid range */
const clampRatio = (r: number): number =>
  Math.max(MIN_SPLIT_RATIO, Math.min(MAX_SPLIT_RATIO, r));

export const useSidebarStore = create<SidebarState>()(
  persist(
    (set) => ({
      // Expanded by default so first-time users can see the Object Explorer
      // exists (they can always collapse it; the choice persists).
      isCollapsed: false,
      width: DEFAULT_SIDEBAR_WIDTH,
      sidebarSplitRatio: DEFAULT_SPLIT_RATIO,
      explorerCollapsed: false,
      scriptsCollapsed: false,
      hasHydrated: false,

      toggleCollapsed: () => {
        set((state) => {
          const newCollapsed = !state.isCollapsed;
          trackSidebarEvent(newCollapsed ? 'collapse' : 'expand');
          return { isCollapsed: newCollapsed };
        });
      },

      expand: () => {
        trackSidebarEvent('expand');
        set({ isCollapsed: false });
      },

      collapse: () => {
        trackSidebarEvent('collapse');
        set({ isCollapsed: true });
      },

      setWidth: (width: number) => {
        const clampedWidth = Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, width));
        set({ width: clampedWidth });
      },

      setSplitRatio: (ratio: number) => {
        set({ sidebarSplitRatio: clampRatio(ratio) });
      },

      toggleExplorerCollapsed: () => {
        set((state) => ({ explorerCollapsed: !state.explorerCollapsed }));
      },

      toggleScriptsCollapsed: () => {
        set((state) => ({ scriptsCollapsed: !state.scriptsCollapsed }));
      },

      expandScriptsPanel: () => {
        set({ scriptsCollapsed: false });
      },

      setHasHydrated: (hydrated: boolean) => {
        set({ hasHydrated: hydrated });
      },
    }),
    {
      name: 'sql-studio-sidebar',
      // Crash-proof write (core/storage/guardedJsonStorage.ts): a full origin must not throw out of
      // set(). No toast here — this is panel geometry the user re-adjusts in one drag; the drop is
      // still reported to telemetry and the console.
      storage: createGuardedJsonStorage(),
      partialize: (state) => ({
        isCollapsed: state.isCollapsed,
        width: state.width,
        sidebarSplitRatio: state.sidebarSplitRatio,
        explorerCollapsed: state.explorerCollapsed,
        scriptsCollapsed: state.scriptsCollapsed,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

export {
  DEFAULT_SIDEBAR_WIDTH,
  MIN_SIDEBAR_WIDTH,
  MAX_SIDEBAR_WIDTH,
  DEFAULT_SPLIT_RATIO,
  MIN_SPLIT_RATIO,
  MAX_SPLIT_RATIO,
};
